/**
 * Le contrat des états, des exports et du pilotage, côté navigateur
 * (`EX-BO-004`, stories 12.6 à 12.10).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `ReportingMessages`, sans rien y ajouter.
 * Un contrôle confronte champ par champ cette déclaration au **fichier Java
 * lui-même** : recopier un contrat, c'est en faire une seconde source de vérité
 * qui vieillira, et un champ renommé d'un seul côté produit un écran vide
 * **sans erreur**.
 *
 * ## Ce que ce chantier referme
 *
 * `ReportingApi` servait **quatre routes, éprouvées, et appelées par aucun
 * écran** — la famille de défauts que `D-25`, `D-30`, `D-35`, `D-45` et `D-57`
 * décrivent, et dont le contrôle des routes les nommait déjà :
 *
 *  - `GET /reporting/definitions` — le catalogue des états configurés ;
 *  - `GET /reporting/statements/{code}` — la production d'un état sur une
 *    période, en **données** ou en **fichier** ;
 *  - `GET /reporting/integrity` — le verdict du chaînage, recalculé depuis
 *    l'origine ;
 *  - `GET /reporting/statements` — les derniers états produits, sans contenu.
 *
 * ## Une seule route rend la table ET le fichier, et c'est le point
 *
 * `download=true` rend les octets du format ; sans lui, la même route rend la
 * table en JSON. **Le même contenu est archivé dans les deux cas** : ce que
 * l'écran montre et ce que le contrôleur enregistre portent le même sceau. Une
 * empreinte qui ne vaudrait que pour l'un des deux ne prouverait rien de
 * l'autre — et c'est précisément ce qu'un contrôleur financier vient chercher.
 *
 * C'est pourquoi l'export **ne se fabrique pas ici**. Les trois autres écrans
 * de ce back-office composent leur CSV dans le navigateur avec `toCsv` ; le
 * faire ici produirait un fichier que le serveur n'a jamais vu, dont
 * l'empreinte ne correspondrait à aucune archive, et qui se présenterait
 * pourtant comme un état scellé.
 *
 * ## Le motif de consultation est une constante, et non une saisie
 *
 * C'est la conduite de la file de double validation, du rapprochement et du
 * listage du journal, et la raison est la même : un contrôleur ouvre le
 * catalogue toujours pour la même raison — le consulter. Une invite par
 * ouverture coûterait une frappe pour une phrase identique.
 *
 * **Ce qui distingue une production d'une autre est déjà journalisé** : le
 * serveur écrit le code de l'état, la période et le format. Le motif n'a pas à
 * redire ce que les critères disent.
 */

import type { ApiBytes, ApiClient } from './api-client.js';

/** Le chemin du catalogue, tel que `ReportingApi` le sert. */
export const reportDefinitionsPath = '/api/backoffice/reporting/definitions';

/** Le chemin de la production d'un état — le code de l'état le complète. */
export const reportStatementPath = '/api/backoffice/reporting/statements';

/** Le chemin du verdict de chaînage. */
export const reportIntegrityPath = '/api/backoffice/reporting/integrity';

/**
 * Le motif écrit au journal pour toute lecture de ce module.
 *
 * Voir l'en-tête : une constante, parce que la consultation du catalogue et la
 * production d'un état sont le geste ordinaire de ce module, et que le serveur
 * journalise déjà ce qui les distingue.
 */
export const reportingReason = 'etats-et-pilotage';

/**
 * La borne par défaut de l'historique des états produits.
 *
 * Le serveur accepte de 1 à 500 et refuse bruyamment au-delà
 * (`BackOfficeRequestRejectedException`). Cinquante est **sa** valeur par
 * défaut, reprise ici pour que l'écran demande ce qu'il affiche plutôt que de
 * laisser le serveur choisir à sa place : une borne implicite est une borne
 * qu'on ne relit pas le jour où elle change.
 */
export const producedStatementsLimit = 50;

/** Une colonne configurée d'un état. Suit `ReportingMessages.ColumnView`. */
export interface ReportColumn {
  readonly position: number;
  readonly fieldCode: string;
  readonly headerLabel: string;
}

/**
 * Un état configuré, tel que le catalogue le propose. Suit
 * `ReportingMessages.ReportDefinitionView`.
 *
 * `chartCode` et `retentionDays` peuvent manquer, et chacun pour une raison qui
 * se lit : tous les états n'alimentent pas un plan comptable, et tous ne sont
 * pas soumis à une durée de conservation. Les rendre à zéro ou à la chaîne vide
 * ferait passer « non applicable » pour une valeur.
 */
export interface ReportDefinition {
  readonly code: string;
  readonly label: string;
  readonly kind: string;
  readonly formatCode: string;
  readonly chartCode: string | null;
  readonly periodicity: string;
  readonly retentionDays: number | null;
  readonly active: boolean;
  readonly columns: readonly ReportColumn[];
}

/**
 * Un état produit, rendu **en données** plutôt qu'en fichier. Suit
 * `ReportingMessages.ProducedReportView`.
 *
 * `totalRows` est ce que l'état porte **en tout**, jamais la taille de ce qui
 * est affiché : l'écran ne recalcule aucune valeur à partir de `rows`, parce
 * qu'un total recalculé par le navigateur sur la page affichée est un total de
 * la page — un chiffre faux présenté comme un fait.
 *
 * `servedByReplica` dit si la réplique de lecture a servi. Faux n'est pas une
 * panne : sans `JELIKO_REPORTING_DB_URL`, il n'y a pas de réplique, et l'état
 * porte écrit qu'il a été produit sur la base principale. L'écran le montre au
 * lieu de le taire, parce que c'est une information d'exploitation — un export
 * lourd servi par la principale prend des connexions à ce qui sert les
 * retraits.
 *
 * `notices` porte ce que le serveur a voulu dire de cette production, et il
 * peut être vide. Un tableau vide n'est pas « rien à signaler » qu'on aurait
 * inventé : c'est le serveur qui n'a rien signalé.
 */
export interface ProducedReport {
  readonly reportCode: string;
  readonly formatCode: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly totalRows: number;
  readonly chainIndex: number;
  readonly contentSha256: string;
  readonly rowHash: string;
  readonly retentionUntil: string;
  readonly servedByReplica: boolean;
  readonly producedAt: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
  readonly notices: readonly string[];
}

/**
 * Le verdict du chaînage des états produits. Suit
 * `ReportingMessages.ChainVerificationView`.
 *
 * **`intact: true` ne se lit pas seul.** `linksChecked` dit sur combien de
 * maillons le verdict porte : un chaînage intact sur zéro maillon est vrai et
 * ne démontre rien, exactement comme la somme nulle du contrôle d'équilibre du
 * grand livre. L'écran affiche donc toujours les deux, et jamais la pastille
 * verte seule.
 *
 * `brokenAtIndex` et `reason` ne sont servis que lorsque la chaîne est rompue.
 */
export interface ChainVerification {
  readonly intact: boolean;
  readonly linksChecked: number;
  readonly brokenAtIndex: number | null;
  readonly reason: string | null;
  readonly headIndex: number;
  readonly headHash: string;
}

/**
 * Un état archivé, sans son contenu. Suit
 * `ReportingMessages.ArchivedStatementView`.
 *
 * C'est la mémoire du module : qui a produit quoi, sur quelle période, et avec
 * quelle empreinte. `contentSha256` est ce qu'un contrôleur confronte au
 * fichier qu'il a gardé — sans quoi « scellé » est un mot sur un écran.
 */
export interface ArchivedStatement {
  readonly id: string;
  readonly chainIndex: number;
  readonly reportCode: string;
  readonly formatCode: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly rowCount: number;
  readonly contentSha256: string;
  readonly rowHash: string;
  readonly retentionUntil: string;
  readonly servedByReplica: boolean;
  readonly producedAt: string;
}

/** Ce qu'une production d'état exige. Le format est facultatif. */
export interface StatementQuery {
  /** Le code de l'état, tel que le catalogue le donne. */
  readonly code: string;
  /** Début de la période, en ISO `AAAA-MM-JJ`. */
  readonly from: string;
  /** Fin de la période, incluse, en ISO `AAAA-MM-JJ`. */
  readonly to: string;
  /** Le format demandé. Absent : celui que la définition porte. */
  readonly format?: string;
}

/**
 * Lit le catalogue des états configurés.
 *
 * Aucune capture d'erreur, pour la raison déjà écrite sur le journal d'audit :
 * rendre une liste vide ferait confondre « aucun état n'est configuré » — un
 * fait — avec « le serveur a refusé », qui n'en est pas un. Le premier appelle
 * un paramétrage, le second un administrateur.
 */
export async function fetchReportDefinitions(
  client: ApiClient,
  reason: string = reportingReason,
): Promise<readonly ReportDefinition[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly ReportDefinition[]>(
    `${reportDefinitionsPath}?${parametres.toString()}`,
  );
}

/**
 * Produit un état et rend sa **table**.
 *
 * L'état est archivé et chaîné du côté serveur par cet appel : produire n'est
 * pas une lecture inoffensive, et c'est pour cela que la période part telle
 * quelle plutôt que d'être « corrigée » ici. Une borne ajustée par le
 * navigateur scellerait une période que personne n'a demandée.
 */
export async function produceStatement(
  client: ApiClient,
  query: StatementQuery,
  reason: string = reportingReason,
): Promise<ProducedReport> {
  const produit = await client.request<ProducedReport>(
    `${reportStatementPath}/${encodeURIComponent(query.code)}?${parametresDe(query, reason)}`,
  );
  return exigerUnEtat(produit);
}

/**
 * Produit le même état et rend son **fichier**, tel que le serveur le scelle.
 *
 * Voir l'en-tête : l'export n'est pas composé dans le navigateur. Ce que cette
 * fonction rend est ce que le serveur a archivé, octet pour octet — c'est la
 * seule forme sous laquelle l'empreinte affichée à l'écran vaut pour le fichier
 * que le contrôleur enregistre.
 */
export async function downloadStatement(
  client: ApiClient,
  query: StatementQuery,
  reason: string = reportingReason,
): Promise<ApiBytes> {
  const parametres = new URLSearchParams(parametresDe(query, reason));
  parametres.set('download', 'true');
  return client.requestBytes(
    `${reportStatementPath}/${encodeURIComponent(query.code)}?${parametres.toString()}`,
  );
}

/** Demande le verdict du chaînage, recalculé depuis l'origine. */
export async function fetchChainVerification(
  client: ApiClient,
  reason: string = reportingReason,
): Promise<ChainVerification> {
  const parametres = new URLSearchParams({ reason });
  return client.request<ChainVerification>(`${reportIntegrityPath}?${parametres.toString()}`);
}

/** Liste les derniers états produits, sans leur contenu. */
export async function fetchProducedStatements(
  client: ApiClient,
  limit: number = producedStatementsLimit,
  reason: string = reportingReason,
): Promise<readonly ArchivedStatement[]> {
  const parametres = new URLSearchParams({ reason, limit: String(limit) });
  return client.request<readonly ArchivedStatement[]>(
    `${reportStatementPath}?${parametres.toString()}`,
  );
}

/** Les paramètres communs aux deux formes d'une production. */
function parametresDe(query: StatementQuery, reason: string): string {
  const parametres = new URLSearchParams({ from: query.from, to: query.to, reason });
  // Un format vide n'est PAS envoyé vide : `format=` serait un choix que le
  // serveur devrait interpréter, là où son absence dit sans ambiguïté « celui
  // que la définition porte ».
  if (query.format !== undefined && query.format !== '') parametres.set('format', query.format);
  return parametres.toString();
}

/**
 * Refuse ce qui n'est **pas** un état produit.
 *
 * Le contrat servi est éprouvé champ par champ contre le `record` Java, mais
 * cela vaut pour ce que le serveur *déclare* ; à l'exécution, une réponse d'une
 * autre forme — une route mal branchée, un mandataire qui rend sa propre page —
 * arriverait ici sans rien dire. L'écran s'écroulerait sur une lecture de
 * `rows.length`, et le contrôleur verrait un écran blanc là où il attend un
 * état scellé.
 *
 * Le refus est donc explicite, et il devient un message : mieux vaut « le
 * serveur a répondu quelque chose d'inutilisable » qu'une table vide, qui se
 * lirait comme « cet état ne porte rien sur cette période ».
 */
function exigerUnEtat(produit: ProducedReport): ProducedReport {
  if (!Array.isArray(produit?.rows) || !Array.isArray(produit?.headers)
    || typeof produit?.contentSha256 !== 'string') {
    throw new Error(
      'ÉCHEC : la réponse n’est pas un état produit — `headers`, `rows` et ' +
        'l’empreinte sont ce qui le définit.',
    );
  }
  return produit;
}
